import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Category, CategoryFormData } from '../types/category';
import CategoryForm from '../components/CategoryForm';
import CategoryList from '../components/CategoryList';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { toast } from "sonner";

const CategoryPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; category: Category | null }>({
    isOpen: false,
    category: null,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('image_category')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (formData: CategoryFormData) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('image_category')
        .insert([formData])
        .select();

      if (error) throw error;

      setCategories([...categories, data[0]]);
      setShowForm(false);
      toast.success('Category created successfully!');
    } catch (error) {
      toast.error('Error creating category');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (formData: CategoryFormData) => {
    if (!editingCategory?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('image_category')
        .update(formData)
        .eq('id', editingCategory.id)
        .select();

      if (error) throw error;

      setCategories(categories.map(cat => cat.id === editingCategory.id ? data[0] : cat));
      setEditingCategory(null);
      setShowForm(false);
      toast.success('Category updated successfully!');
    } catch (error) {
      toast.error('Error updating category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setDeleteModal({ isOpen: true, category });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.category?.id) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('image_category')
        .delete()
        .eq('id', deleteModal.category.id);

      if (error) throw error;

      setCategories(categories.filter(cat => cat.id !== deleteModal.category?.id));
      setDeleteModal({ isOpen: false, category: null });
      toast.success('Category deleted successfully!');
    } catch (error) {
      toast.error('Error deleting category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, category: null });
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Categories</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          {showForm ? 'Cancel' : 'Create Category'}
        </button>
      </div>

      {showForm && (
        <CategoryForm
          onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory}
          onCancel={handleCancel}
          loading={loading}
          initialData={editingCategory}
        />
      )}

      <CategoryList
        categories={categories}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        itemName={deleteModal.category?.category}
        loading={loading}
      />
    </div>
  );
};

export default CategoryPage;

